import { useEffect, useMemo, useState, type ReactNode } from "react";
import "../dashboard.css";
import {
  FiArrowDown,
  FiArrowUp,
  FiCalendar,
  FiChevronRight,
  FiEye,
  FiEyeOff,
  FiPieChart,
  FiSliders,
  FiTag,
  FiTarget,
} from "react-icons/fi";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { DomainChatTrigger } from "@/domains/chat/components/DomainChatTrigger";
import { useStandardPeriodRange } from "@/hooks/useStandardPeriodRange";
import {
  useBillableTransactions,
  useMonthlyBalances,
  useRepeatedTransactions,
} from "@/hooks/useTransactions.ts";
import { useCategoriesBalance } from "@/hooks/useCategories";
import { useBudget } from "@/hooks/useBudget";
import { useSavings, type SavingPlanBalance } from "@/hooks/useSavings";
import { useAuthStore } from "@/stores/useAuthStore";
import { parseDateLocal } from "@/utils/dateUtils";
import { useTranslation } from "react-i18next";
import { flowArrow, formatCurrency, formatDate, formatNetAmount, formatSignedCurrency } from "@/utils/formatters";
import { calculateFinancialTotals } from "@/utils/financialTotals";
import { ActivityList, type ActivityGroup, type ActivityItem } from "../components/ActivityList";
import { GlanceList, type GlanceItem } from "../components/GlanceList";
import { IncomeExpenseCard, type IncomeExpenseWeek } from "../components/IncomeExpenseCard";
import { PeriodSummary } from "../components/PeriodSummary";
import { PositionTrend, type PositionTrendLabels } from "../components/PositionTrend";
import { RepeatList, type RepeatItem } from "../components/RepeatList";
import { ShareCard, type ShareItem } from "../components/ShareCard";
import { CollapsibleSection, CycleLink, PillButton } from "@/components/ds";

interface WeekBucket {
  label: string;
  inflow: number;
  outflow: number;
  isCurrentWeek: boolean;
}

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const isSameDay = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const buildBucketLabel = (start: Date, end: Date) => {
  if (isSameDay(start, end)) {
    return formatDate(start, undefined, { day: "numeric", month: "short" });
  }

  const startDay = formatDate(start, undefined, { day: "numeric" });
  const endDay = formatDate(end, undefined, { day: "numeric" });
  const month = formatDate(end, undefined, { month: "short" });

  return `${startDay}–${endDay} ${month}`;
};

const DEFAULT_DASHBOARD_PANEL_ORDER = [
  "balance",
  "cashFlow",
  "activity",
  "breakdown",
  "repeated",
] as const;

type DashboardPanelId = (typeof DEFAULT_DASHBOARD_PANEL_ORDER)[number];

/** Paneles que se ponen en par (dos columnas en lg) cuando son contiguos. */
const PAIRABLE_PANEL_IDS: DashboardPanelId[] = ["breakdown", "repeated"];

interface DashboardLayout {
  order: DashboardPanelId[];
  visible: Record<DashboardPanelId, boolean>;
  expanded: Record<DashboardPanelId, boolean>;
}

const DEFAULT_DASHBOARD_LAYOUT: DashboardLayout = {
  order: [...DEFAULT_DASHBOARD_PANEL_ORDER],
  visible: {
    balance: true,
    breakdown: true,
    repeated: true,
    cashFlow: true,
    activity: true,
  },
  expanded: {
    balance: true,
    breakdown: true,
    repeated: true,
    cashFlow: true,
    activity: true,
  },
};

const isDashboardPanelId = (value: unknown): value is DashboardPanelId =>
  typeof value === "string" &&
  DEFAULT_DASHBOARD_PANEL_ORDER.includes(value as DashboardPanelId);

const readDashboardLayout = (storageKey: string): DashboardLayout => {
  const storedLayout = window.localStorage.getItem(storageKey);

  if (!storedLayout) return DEFAULT_DASHBOARD_LAYOUT;

  try {
    const layout = JSON.parse(storedLayout) as Partial<DashboardLayout>;
    const order = layout.order;

    if (
      !Array.isArray(order) ||
      order.length !== DEFAULT_DASHBOARD_PANEL_ORDER.length ||
      !DEFAULT_DASHBOARD_PANEL_ORDER.every((panelId) => order.includes(panelId)) ||
      !order.every(isDashboardPanelId)
    ) {
      return DEFAULT_DASHBOARD_LAYOUT;
    }

    return {
      order,
      visible: {
        ...DEFAULT_DASHBOARD_LAYOUT.visible,
        ...layout.visible,
      },
      expanded: {
        ...DEFAULT_DASHBOARD_LAYOUT.expanded,
        ...layout.expanded,
      },
    };
  } catch {
    return DEFAULT_DASHBOARD_LAYOUT;
  }
};

export const DashboardView = () => {
  const { t } = useTranslation(["dashboard", "common", "transactions"]);
  const { user, profileHydrated } = useAuthStore();
  const dashboardLayoutKey = user?.id
    ? `hey-jerry-dashboard-layout:v1:${user.id}`
    : null;
  const [dashboardLayout, setDashboardLayout] = useState<DashboardLayout>(
    DEFAULT_DASHBOARD_LAYOUT,
  );
  const [loadedLayoutKey, setLoadedLayoutKey] = useState<string | null>(null);
  const [cycleFilterOpen, setCycleFilterOpen] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);

  useEffect(() => {
    if (!dashboardLayoutKey) {
      setDashboardLayout(DEFAULT_DASHBOARD_LAYOUT);
      setLoadedLayoutKey(null);
      return;
    }

    setDashboardLayout(readDashboardLayout(dashboardLayoutKey));
    setLoadedLayoutKey(dashboardLayoutKey);
  }, [dashboardLayoutKey]);

  useEffect(() => {
    if (!dashboardLayoutKey || loadedLayoutKey !== dashboardLayoutKey) return;

    window.localStorage.setItem(dashboardLayoutKey, JSON.stringify(dashboardLayout));
  }, [dashboardLayout, dashboardLayoutKey, loadedLayoutKey]);
  const { selectedPeriod, setSelectedPeriod, customStart, setCustomStart, customEnd, setCustomEnd, selectedRange } =
    useStandardPeriodRange("this-month", "dashboard");
  const cycleOptions = useMemo(
    () => [
      { id: "this-month" as const, label: t("common.periods.thisMonth") },
      { id: "last-month" as const, label: t("common.periods.lastMonth") },
      { id: "custom" as const, label: t("common.periods.custom") },
    ],
    [t],
  );
  const selectedPeriodLabel =
    cycleOptions.find((option) => option.id === selectedPeriod)?.label ??
    t("common.periods.thisMonth");

  const currentRangeParams = useMemo(
    () => ({
      billable_date_gte: selectedRange.start,
      billable_date_lte: selectedRange.end,
    }),
    [selectedRange.end, selectedRange.start],
  );

  // Wait for the saved layout before requesting data exclusive to optional panels.
  const layoutReady = dashboardLayoutKey ? loadedLayoutKey === dashboardLayoutKey : profileHydrated;
  const positionEnabled = layoutReady && dashboardLayout.visible.balance && dashboardLayout.expanded.balance;
  const repeatedEnabled = layoutReady && dashboardLayout.visible.repeated && dashboardLayout.expanded.repeated;

  const { data: categoriesBalanceData, error: categoriesBalanceError } = useCategoriesBalance({
    ...currentRangeParams,
    category_total_expense: "desc",
  });
  const { data: monthlyBalanceData, error: monthlyBalanceError } = useMonthlyBalances(currentRangeParams, { enabled: positionEnabled });
  const { data: billableTransactionsData, error: transactionsError } = useBillableTransactions({
    ...currentRangeParams,
    transaction_date: "desc",
  });
  const { data: repeatedTransactionsData, error: repeatedTransactionsError } = useRepeatedTransactions({ enabled: repeatedEnabled });
  const { data: budgetData } = useBudget(currentRangeParams);
  const { data: savingsData } = useSavings();
  const currentPeriodTransactions = useMemo(
    () => billableTransactionsData ?? [],
    [billableTransactionsData],
  );
  const hasDashboardData = Boolean(
    categoriesBalanceData || monthlyBalanceData || billableTransactionsData || repeatedTransactionsData,
  );
  const repeatedTransactions = useMemo(
    () =>
      [...(repeatedTransactionsData ?? [])]
        .sort((left, right) => right.trx_count - left.trx_count),
    [repeatedTransactionsData],
  );
  const monthlyBalances = useMemo(
    () => monthlyBalanceData?.monthly_balances ?? [],
    [monthlyBalanceData],
  );
  const currentPeriod = useMemo(() => {
    const totals = calculateFinancialTotals(currentPeriodTransactions);
    return { balance: totals.net, deposit: totals.income, expense: totals.expense };
  }, [currentPeriodTransactions]);

  const weekBuckets = useMemo<WeekBucket[]>(() => {
    const startDate = parseDateLocal(selectedRange.start);
    const endDate = parseDateLocal(selectedRange.end);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return [];

    const today = startOfDay(new Date());

    const buckets: Array<{
      start: Date;
      end: Date;
      inflow: number;
      outflow: number;
    }> = [];

    let cursor = startOfDay(startDate);
    const finalDay = startOfDay(endDate);

    while (cursor <= finalDay) {
      const bucketStart = new Date(cursor);
      const bucketEnd = startOfDay(
        new Date(Math.min(addDays(bucketStart, 6).getTime(), finalDay.getTime())),
      );

      buckets.push({
        start: bucketStart,
        end: bucketEnd,
        inflow: 0,
        outflow: 0,
      });

      cursor = addDays(bucketEnd, 1);
    }

    currentPeriodTransactions.forEach((transaction) => {
      const transactionDate = startOfDay(parseDateLocal(transaction.transaction_date));
      const bucket = buckets.find(
        (item) => transactionDate >= item.start && transactionDate <= item.end,
      );

      if (!bucket) return;

      if (transaction.type_id === 1) bucket.inflow += Number(transaction.amount || 0);
      if (transaction.type_id === 2) bucket.outflow += Number(transaction.amount || 0);
    });

    return buckets.map((bucket) => ({
      label: buildBucketLabel(bucket.start, bucket.end),
      inflow: bucket.inflow,
      outflow: bucket.outflow,
      isCurrentWeek: today >= bucket.start && today <= bucket.end,
    }));
  }, [currentPeriodTransactions, selectedRange.end, selectedRange.start]);

  /** Semanas con movimientos: filas del desglose semanal. */
  const incomeExpenseWeeks = useMemo<IncomeExpenseWeek[]>(
    () =>
      weekBuckets.flatMap((bucket, index) =>
        bucket.inflow > 0 || bucket.outflow > 0
          ? [
              {
                id: String(index),
                label: bucket.label,
                income: bucket.inflow,
                expense: bucket.outflow,
                current: bucket.isCurrentWeek,
              },
            ]
          : [],
      ),
    [weekBuckets],
  );

  /** Semanas sin movimientos: rangos para el aviso de semanas vacías. */
  const emptyWeekLabels = useMemo(
    () =>
      weekBuckets
        .filter((bucket) => bucket.inflow === 0 && bucket.outflow === 0)
        .map((bucket) => bucket.label),
    [weekBuckets],
  );

  const insight = useMemo(() => {
    if (currentPeriodTransactions.length === 0) {
      return t("dashboard.noRecentActivity");
    }

    const profitableBuckets = weekBuckets.filter((bucket) => bucket.inflow >= bucket.outflow).length;
    const totalBuckets = weekBuckets.length;

    if (currentPeriod.balance >= 0) {
      return t("dashboard.insightPositiveWeeks", {
        count: profitableBuckets,
        total: totalBuckets,
      });
    }

    return t("dashboard.insightNegativeGeneric");
  }, [
    weekBuckets,
    currentPeriod.balance,
    currentPeriodTransactions.length,
    t,
  ]);

  const topCategories = useMemo(
    () => (categoriesBalanceData?.categories_balance ?? []).slice(0, 3),
    [categoriesBalanceData],
  );

  const shareItems = useMemo<ShareItem[]>(
    () =>
      topCategories.map((category) => ({
        id: String(category.id),
        label: category.name,
        percent: category.percentage_of_total_expense,
      })),
    [topCategories],
  );

  const repeatItems = useMemo<RepeatItem[]>(
    () =>
      repeatedTransactions.map((transaction, index) => ({
        id: `${transaction.name}-${index}`,
        name: transaction.name,
        times: transaction.trx_count,
        total: formatCurrency(transaction.total_amount),
      })),
    [repeatedTransactions],
  );

  const activityGroups = useMemo<ActivityGroup[]>(() => {
    const today = startOfDay(new Date());
    const yesterday = addDays(today, -1);
    const weekStart = addDays(today, -6);
    const recent = currentPeriodTransactions.slice(0, 5);

    const groups: ActivityGroup[] = [
      { label: t("dashboard.activityToday"), items: [] },
      { label: t("dashboard.activityYesterday"), items: [] },
      { label: t("dashboard.activityThisWeek"), items: [] },
      { label: t("dashboard.activityEarlier"), items: [] },
    ];

    recent.forEach((transaction) => {
      const date = startOfDay(parseDateLocal(transaction.transaction_date));
      const isExpense = transaction.type_id === 2;
      const item: ActivityItem = {
        id: String(transaction.id),
        name: transaction.name,
        meta: `${isExpense ? t("dashboard.activityTypeOutflow") : t("dashboard.activityTypeInflow")} · ${formatDate(date, undefined, {
          day: "numeric",
          month: "short",
        })}`,
        flow: isExpense ? "expense" : "income",
        amount: Math.abs(Number(transaction.amount || 0)),
      };

      let index = 3;
      if (isSameDay(date, today)) index = 0;
      else if (isSameDay(date, yesterday)) index = 1;
      else if (date >= weekStart) index = 2;

      groups[index].items.push(item);
    });

    return groups.filter((group) => group.items.length > 0);
  }, [currentPeriodTransactions, t]);

  /** Fila de presupuestos: porcentaje gastado del total presupuestado del ciclo. Se omite sin datos. */
  const budgetGlance = useMemo<GlanceItem | null>(() => {
    if (!budgetData || budgetData.budgets_balance.length === 0 || budgetData.total_budgeted <= 0) {
      return null;
    }

    const percent = Math.round((budgetData.total_spent / budgetData.total_budgeted) * 100);

    return {
      id: "budgets",
      icon: <FiPieChart aria-hidden="true" className="h-5 w-5" />,
      color: "mist",
      title: t("dashboard.glanceBudgets"),
      detail: t("dashboard.glanceBudgetUsed", { percent }),
      amount: formatCurrency(budgetData.remaining_budget),
      to: "/budget",
    };
  }, [budgetData, t]);

  /** Meta principal: la de mayor monto objetivo. Se omite si no hay metas con objetivo. */
  const goalGlance = useMemo<GlanceItem | null>(() => {
    const plans = savingsData?.saving_plans_balance ?? [];
    const mainPlan = plans.reduce<SavingPlanBalance | null>(
      (best, plan) =>
        plan.target_amount > 0 && (!best || plan.target_amount > best.target_amount) ? plan : best,
      null,
    );

    if (!mainPlan) return null;

    const percent = Math.round((mainPlan.saved_amount / mainPlan.target_amount) * 100);

    return {
      id: "goal",
      icon: <FiTarget aria-hidden="true" className="h-5 w-5" />,
      color: "lilac",
      title: t("dashboard.glanceGoal"),
      detail: `${mainPlan.name} · ${percent}%`,
      amount: formatCurrency(mainPlan.saved_amount),
      to: "/savings",
    };
  }, [savingsData, t]);

  /** Categoría que más pesó en el gasto del ciclo. Se omite sin categorías. */
  const categoryGlance = useMemo<GlanceItem | null>(() => {
    const top = topCategories[0];

    if (!top) return null;

    return {
      id: "top-category",
      icon: <FiTag aria-hidden="true" className="h-5 w-5" />,
      color: "rose",
      title: t("dashboard.glanceTopCategory"),
      detail: `${top.name} · ${Math.round(top.percentage_of_total_expense)}%`,
      amount: formatSignedCurrency(-Math.abs(top.category_total_expense)),
      to: "/categories",
    };
  }, [topCategories, t]);

  const glanceItems = useMemo(
    () =>
      [budgetGlance, goalGlance, categoryGlance].filter(
        (item): item is GlanceItem => item !== null,
      ),
    [budgetGlance, goalGlance, categoryGlance],
  );

  const positionLabels = useMemo<PositionTrendLabels>(
    () => ({
      latest: t("dashboard.latest"),
      empty: t("dashboard.positionTrendEmpty"),
      describe: (description) => t("dashboard.positionTrendDescribe", { ...description }),
      role: t("dashboard.positionTrendRole"),
      keyboardHint: t("dashboard.positionTrendKeyboardHint"),
      monthValue: (month, value) => t("dashboard.positionTrendMonthValue", { month, value }),
    }),
    [t],
  );

  const movePanel = (panelId: DashboardPanelId, offset: -1 | 1) => {
    setDashboardLayout((current) => {
      const currentIndex = current.order.indexOf(panelId);
      const nextIndex = currentIndex + offset;

      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= current.order.length) {
        return current;
      }

      const order = [...current.order];
      [order[currentIndex], order[nextIndex]] = [order[nextIndex], order[currentIndex]];
      return { ...current, order };
    });
  };

  const setPanelVisibility = (panelId: DashboardPanelId, visible: boolean) => {
    setDashboardLayout((current) => ({
      ...current,
      visible: { ...current.visible, [panelId]: visible },
    }));
  };

  const setPanelExpanded = (panelId: DashboardPanelId, expanded: boolean) => {
    setDashboardLayout((current) => {
      if (current.expanded[panelId] === expanded) return current;

      return {
        ...current,
        expanded: { ...current.expanded, [panelId]: expanded },
      };
    });
  };

  const panelLabels: Record<DashboardPanelId, string> = {
    balance: t("dashboard.positionTrendTitle"),
    breakdown: t("dashboard.breakdownTitle"),
    repeated: t("dashboard.repeatedTransactionsTitle"),
    cashFlow: `${t("dashboard.inflowLabel")} vs ${t("dashboard.outflowLabel")}`,
    activity: t("dashboard.activityTitle"),
  };
  const visiblePanelIds = dashboardLayout.order.filter(
    (panelId) => dashboardLayout.visible[panelId],
  );

  /** Agrupa paneles visibles: los contiguos de `PAIRABLE_PANEL_IDS` van en par. */
  const panelGroups = visiblePanelIds.reduce<DashboardPanelId[][]>((groups, panelId) => {
    const last = groups[groups.length - 1];

    if (
      last &&
      last.length === 1 &&
      PAIRABLE_PANEL_IDS.includes(last[0]) &&
      PAIRABLE_PANEL_IDS.includes(panelId)
    ) {
      last.push(panelId);
    } else {
      groups.push([panelId]);
    }

    return groups;
  }, []);

  const renderPanel = (panelId: DashboardPanelId): ReactNode => {
    const collapsible = {
      open: dashboardLayout.expanded[panelId],
      onOpenChange: (open: boolean) => setPanelExpanded(panelId, open),
      toggleLabel: (open: boolean) =>
        t(open ? "dashboard.collapseSection" : "dashboard.expandSection", {
          section: panelLabels[panelId],
        }),
    };

    switch (panelId) {
      case "balance":
        return (
          <CollapsibleSection
            title={t("dashboard.positionTrendTitle")}
            subtitle={t("dashboard.positionTrendSubtitle")}
            {...collapsible}
          >
            <PositionTrend monthlyBalances={monthlyBalances} labels={positionLabels} />
          </CollapsibleSection>
        );
      case "cashFlow":
        return (
          <CollapsibleSection
            title={panelLabels.cashFlow}
            subtitle={insight}
            {...collapsible}
          >
            <IncomeExpenseCard
              income={Math.abs(currentPeriod.deposit)}
              expense={Math.abs(currentPeriod.expense)}
              weeks={incomeExpenseWeeks}
              emptyWeeks={emptyWeekLabels}
              netLabel={t("dashboard.cycleNet")}
              inflowLabel={t("dashboard.inflowLabel")}
              outflowLabel={t("dashboard.outflowLabel")}
              currentWeekLabel={t("dashboard.currentWeek")}
              emptyWeeksLabel={(count, ranges) =>
                t("dashboard.emptyWeeksSummary", { count, weeks: ranges.join(", ") })
              }
            />
          </CollapsibleSection>
        );
      case "activity":
        if (activityGroups.length === 0) {
          return (
            <div className="rounded-[var(--ds-radius-lg)] bg-[var(--ds-surface-raised)] p-5 text-[var(--ds-ink)] sm:p-6">
              <div className="mb-3 text-sm leading-5 text-[var(--ds-ink-muted)]">{t("dashboard.activityTitle")}</div>
              <p className="m-0 text-base leading-6 text-[var(--ds-ink-muted)]">{t("dashboard.noRecentActivity")}</p>
            </div>
          );
        }
        return (
          <ActivityList
            title={t("dashboard.activityTitle")}
            groups={activityGroups}
            moreLabel={t("dashboard.viewAllActivity")}
            moreTo="/transactions"
            flowSpoken={{
              income: t("dashboard.activityTypeInflow"),
              expense: t("dashboard.activityTypeOutflow"),
            }}
          />
        );
      case "breakdown":
        return (
          <CollapsibleSection
            title={t("dashboard.breakdownTitle")}
            subtitle={t("dashboard.breakdownBody")}
            {...collapsible}
          >
            <ShareCard
              overline={t("dashboard.breakdownOverline", { period: selectedPeriodLabel })}
              items={shareItems}
              valueSuffix={t("dashboard.shareOfSpendingSuffix")}
              emptyLabel={t("dashboard.noBreakdownData")}
            />
          </CollapsibleSection>
        );
      case "repeated":
        return (
          <CollapsibleSection
            title={t("dashboard.repeatedTransactionsTitle")}
            subtitle={t("dashboard.repeatedTransactionsSubtitle")}
            {...collapsible}
          >
            <RepeatList
              items={repeatItems}
              timesLabel={(times) => t("dashboard.repeatedCount", { count: times })}
              moreLabel={t("dashboard.viewAllRepeated")}
              moreTo="/transactions"
              emptyLabel={t("dashboard.noRepeatedTransactionsFound")}
            />
          </CollapsibleSection>
        );
    }
  };

  /** Lista de paneles para personalizar. Se renderiza en el popover (lg) o en la tarjeta del final (móvil). */
  const renderCustomizeList = () => (
    <>
      <p className="mb-3 text-sm leading-5 text-[var(--app-text-secondary)]">
        {t("dashboard.customizeDescription")}
      </p>
      <ul className="space-y-2">
        {dashboardLayout.order.map((panelId, index) => (
          <li
            key={panelId}
            className={`flex items-center gap-1 rounded-[var(--ds-radius-md)] px-2 py-1 transition-colors duration-150 ${dashboardLayout.visible[panelId] ? "bg-[var(--app-segmented-active-bg)]" : "bg-transparent"}`}
          >
            <button
              type="button"
              aria-pressed={dashboardLayout.visible[panelId]}
              aria-label={t(dashboardLayout.visible[panelId] ? "dashboard.hidePanel" : "dashboard.showPanel", { panel: panelLabels[panelId] })}
              onClick={() => setPanelVisibility(panelId, !dashboardLayout.visible[panelId])}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-[var(--app-text-secondary)] transition-colors hover:bg-[var(--app-surface-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--app-focus-ring)]"
            >
              {dashboardLayout.visible[panelId] ? <FiEye aria-hidden="true" className="h-4 w-4" /> : <FiEyeOff aria-hidden="true" className="h-4 w-4" />}
            </button>
            <span className={`min-w-0 flex-1 truncate text-xs font-medium ${dashboardLayout.visible[panelId] ? "text-[var(--app-text-secondary)]" : "text-[var(--app-muted-text)]"}`}>
              {panelLabels[panelId]}
            </span>
            <button
              type="button"
              aria-pressed={dashboardLayout.expanded[panelId]}
              aria-label={t("dashboard.panelOpenOnLoad")}
              title={t("dashboard.panelOpenOnLoad")}
              disabled={!dashboardLayout.visible[panelId]}
              onClick={() => setPanelExpanded(panelId, !dashboardLayout.expanded[panelId])}
              className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-colors disabled:opacity-35 ${dashboardLayout.expanded[panelId] ? "bg-[var(--app-primary-chip-bg)] text-[var(--app-primary)]" : "text-[var(--app-text-secondary)] hover:bg-[var(--app-surface-hover)]"}`}
            >
              <FiChevronRight aria-hidden="true" className={`h-4 w-4 transition-transform ${dashboardLayout.expanded[panelId] ? "rotate-90" : ""}`} />
            </button>
            <button
              type="button"
              onClick={() => movePanel(panelId, -1)}
              disabled={index === 0}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-[var(--app-text-secondary)] transition-colors hover:bg-[var(--app-surface-hover)] disabled:opacity-35 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--app-focus-ring)]"
              aria-label={t("dashboard.movePanelUp", { panel: panelLabels[panelId] })}
            >
              <FiArrowUp aria-hidden="true" className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => movePanel(panelId, 1)}
              disabled={index === dashboardLayout.order.length - 1}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-[var(--app-text-secondary)] transition-colors hover:bg-[var(--app-surface-hover)] disabled:opacity-35 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--app-focus-ring)]"
              aria-label={t("dashboard.movePanelDown", { panel: panelLabels[panelId] })}
            >
              <FiArrowDown aria-hidden="true" className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
    </>
  );

  if (
    !hasDashboardData &&
    (categoriesBalanceError || monthlyBalanceError || transactionsError || repeatedTransactionsError)
  ) {
    return (
      <div className="app-page flex items-center justify-center">
        <div className="app-surface border-[color:color-mix(in_srgb,var(--app-danger)_22%,var(--app-border)_78%)] bg-[color:color-mix(in_srgb,var(--app-danger)_8%,var(--app-surface)_92%)] px-8 py-6 text-center">
          <p className="font-medium text-[var(--app-text)]">{t("dashboard.loadError")}</p>
        </div>
      </div>
    );
  }

  const breakdownLine = `${flowArrow("income")} ${t("dashboard.heroInflow", { amount: formatCurrency(Math.abs(currentPeriod.deposit)) })} · ${flowArrow("expense")} ${t("dashboard.heroOutflow", { amount: formatSignedCurrency(-Math.abs(currentPeriod.expense)) })}`;

  return (
    <div className="app-page dashboard-page overflow-x-hidden lg:overflow-visible">
      <div className="app-container max-w-none overflow-x-hidden lg:overflow-visible">
        <div className="relative mx-auto w-full max-w-2xl min-w-0 overflow-x-hidden md:max-w-none lg:overflow-visible">
          <div className="lg:grid lg:grid-cols-[520px_minmax(0,1fr)] lg:items-start lg:gap-16">
            <aside className="flex flex-col gap-7 pb-6 lg:sticky lg:top-[128px] lg:pb-0">
              <header className="pt-2">
                <h1 className="max-w-[10ch] font-[var(--app-font-display)] text-[clamp(3.5rem,7.2vw,6.5rem)] font-normal leading-[0.92] tracking-[-0.05em] text-[var(--app-text)]">
                  <span className="block">{t("common:labels.greeting")}</span>
                  <span className="block break-words">{user?.firstname || t("common.empty.user")}!</span>
                </h1>
                <div className="mt-6">
                  <DomainChatTrigger domain="transactions" />
                </div>
              </header>

              <PeriodSummary
                label={selectedPeriodLabel}
                value={formatNetAmount(currentPeriod.balance)}
                tone={currentPeriod.balance < 0 ? "negative" : "positive"}
                breakdown={breakdownLine}
                hint={insight}
              />

              <div className="-mt-5 lg:hidden">
                <CycleLink
                  aria-haspopup="menu"
                  aria-expanded={cycleFilterOpen}
                  onClick={() => setCycleFilterOpen((open) => !open)}
                >
                  {t("common.labels.cycle")}: {selectedPeriodLabel}
                </CycleLink>
              </div>

              {glanceItems.length > 0 ? (
                <div className="hidden lg:block">
                  <GlanceList title={t("dashboard.glanceTitle")} items={glanceItems} />
                </div>
              ) : null}
            </aside>

            <div className="mt-9 flex min-w-0 flex-col gap-7 lg:mt-0 lg:gap-8">
              <div
                role="toolbar"
                aria-label={t("dashboard.toolbarLabel")}
                className="relative hidden items-center justify-end gap-3 bg-transparent py-2 lg:sticky lg:top-[104px] lg:z-30 lg:flex"
              >
                <PillButton
                  icon={<FiCalendar aria-hidden="true" className="h-4 w-4" />}
                  aria-haspopup="menu"
                  aria-expanded={cycleFilterOpen}
                  onClick={() => setCycleFilterOpen((open) => !open)}
                >
                  {t("common.labels.cycle")}: {selectedPeriodLabel}
                </PillButton>
                <PillButton
                  icon={<FiSliders aria-hidden="true" className="h-4 w-4" />}
                  aria-haspopup="menu"
                  aria-expanded={customizeOpen}
                  onClick={() => setCustomizeOpen((open) => !open)}
                >
                  {t("dashboard.customizeShort")}
                </PillButton>

                {customizeOpen ? (
                  <div className="absolute right-0 top-full z-20 mt-2 max-h-[calc(100vh-12rem)] w-[min(28rem,calc(100vw-2rem))] overflow-y-auto rounded-[var(--ds-radius-lg)] border border-[var(--app-border)] bg-[var(--app-surface)] p-4 text-[var(--app-text)] shadow-[var(--ds-shadow-2)]">
                    {renderCustomizeList()}
                  </div>
                ) : null}
              </div>

              {cycleFilterOpen ? (
                <section className="w-full lg:max-w-[34rem]" aria-label={t("common.labels.cycle")}>
                  <button
                    type="button"
                    aria-expanded="true"
                    onClick={() => setCycleFilterOpen(false)}
                    className="mb-2 min-h-6 text-left text-[11px] font-medium uppercase leading-[14px] tracking-[0.12em] text-[var(--app-text-secondary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--app-focus-ring)]"
                  >
                    {t("common.labels.cycle")}
                  </button>
                  <SegmentedControl
                    value={selectedPeriod}
                    onChange={setSelectedPeriod}
                    options={cycleOptions}
                    ariaLabel={t("common.labels.cycle")}
                    containerClassName="inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-full bg-[var(--ds-surface)] p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    buttonClassName="min-h-11 shrink-0 rounded-full px-5 py-2 !text-base !font-medium transition-colors duration-150"
                  />
                  {selectedPeriod === "custom" ? (
                    <div className="app-custom-cycle-panel mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <label className="app-domain-sidebar-control-label">
                        <span className="app-domain-sidebar-label mb-0">{t("common.labels.startDate")}</span>
                        <input
                          type="date"
                          value={customStart}
                          max={customEnd}
                          onChange={(event) => setCustomStart(event.target.value)}
                          className="app-domain-sidebar-date-input"
                        />
                      </label>
                      <label className="app-domain-sidebar-control-label">
                        <span className="app-domain-sidebar-label mb-0">{t("common.labels.endDate")}</span>
                        <input
                          type="date"
                          value={customEnd}
                          min={customStart}
                          onChange={(event) => setCustomEnd(event.target.value)}
                          className="app-domain-sidebar-date-input"
                        />
                      </label>
                    </div>
                  ) : null}
                </section>
              ) : null}

              {panelGroups.map((group) =>
                group.length === 2 ? (
                  <div key={group.join("-")} className="grid gap-6 lg:grid-cols-2 lg:items-start">
                    {group.map((panelId) => (
                      <div key={panelId} className="min-w-0">
                        {renderPanel(panelId)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div key={group[0]} className="min-w-0">
                    {renderPanel(group[0])}
                  </div>
                ),
              )}

              {visiblePanelIds.length === 0 ? (
                <p className="rounded-[var(--ds-radius-lg)] border border-dashed border-[var(--app-border-strong)] px-5 py-8 text-center text-sm text-[var(--app-text-secondary)]">
                  {t("dashboard.noVisiblePanels")}
                </p>
              ) : null}

              {/* Móvil: personalizar al final, como la tarjeta del catálogo */}
              <button
                type="button"
                aria-expanded={customizeOpen}
                onClick={() => setCustomizeOpen((open) => !open)}
                className="flex w-full items-center justify-between gap-3 rounded-[var(--ds-radius-lg)] bg-[var(--app-surface)] px-5 py-4 text-left text-base font-medium text-[var(--app-text)] lg:hidden"
              >
                <span>{t("dashboard.customize")}</span>
                <FiChevronRight aria-hidden="true" className="h-5 w-5 shrink-0 text-[var(--app-text-secondary)]" />
              </button>
              {customizeOpen ? (
                <div className="rounded-[var(--ds-radius-lg)] border border-[var(--app-border)] bg-[var(--app-surface)] p-4 text-[var(--app-text)] lg:hidden">
                  {renderCustomizeList()}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
